import React, { Component, ErrorInfo, ReactNode } from 'react';
import { ShieldAlert, Zap, RefreshCw, AlertCircle } from 'lucide-react';

interface Props {
  children: ReactNode;
  onRetryAuth?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class AuthErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('AuthErrorBoundary caught an error during Firebase Auth setup:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    if (this.props.onRetryAuth) {
      this.props.onRetryAuth();
    }
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-indigo-950 text-white flex items-center justify-center p-6 antialiased font-sans">
          <div className="max-w-md w-full bg-indigo-900/95 border border-rose-500/40 rounded-[2.5rem] p-8 shadow-2xl space-y-6 text-center animate-fadeIn">
            <div className="w-16 h-16 mx-auto rounded-3xl bg-rose-500/20 text-rose-400 border border-rose-500/40 flex items-center justify-center shadow-inner">
              <ShieldAlert className="w-8 h-8 stroke-[2.5] animate-bounce" />
            </div>

            <div className="space-y-2">
              <div className="inline-block px-3 py-1 bg-rose-500/20 border border-rose-500/30 rounded-full text-[10px] font-black italic uppercase text-rose-300">
                Firebase Auth Error Boundary
              </div>
              <h2 className="text-xl font-black italic uppercase text-white tracking-tight">
                Authentication System Error
              </h2>
              <p className="text-xs text-indigo-200/90 font-medium leading-relaxed">
                An unexpected exception was caught while initializing or resolving authentication.
              </p>
            </div>

            {this.state.error && (
              <div className="bg-indigo-950/90 p-3.5 rounded-2xl border border-white/10 text-left overflow-x-auto font-mono text-[11px] text-rose-300 max-h-28 overflow-y-auto">
                <div className="flex items-center space-x-1 text-[10px] uppercase font-bold text-rose-400/80 mb-1">
                  <AlertCircle className="w-3 h-3" />
                  <span>Error Details</span>
                </div>
                {this.state.error.message || this.state.error.toString()}
              </div>
            )}

            <div className="space-y-3 pt-2">
              <button
                type="button"
                onClick={this.handleReset}
                className="w-full py-3.5 bg-gradient-to-r from-lime-400 via-emerald-400 to-cyan-400 text-black font-black italic uppercase text-xs rounded-2xl shadow-xl shadow-lime-400/20 flex items-center justify-center space-x-2 transition hover:scale-[1.02]"
              >
                <Zap className="w-4 h-4 fill-black" />
                <span>Retry Authentication Listener</span>
              </button>

              <button
                type="button"
                onClick={() => window.location.reload()}
                className="w-full py-2.5 bg-indigo-950 hover:bg-indigo-900 text-indigo-200 font-bold italic uppercase text-xs rounded-xl border border-white/10 flex items-center justify-center space-x-2 transition"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>Reload Application</span>
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AuthErrorBoundary;
